import styled from "@emotion/styled";
import {
  Avatar,
  Box,
  Button,
  Container,
  CopyToClipboard,
  FlexRow,
  H1,
  H2,
  List,
  Stack,
  Text,
} from "atoms";
import { UserFollows } from "features/follows";
import { Head } from "features/misc";
import {
  OpenCallSubmissionSummary,
  useOpenCallSubmissions,
} from "features/openCalls";
import { UserStatus, useProfile } from "features/profile";
import { ProfileReviews } from "features/reviews/components/ProfileReviews";
import { useParams } from "react-router-dom";
import { ClaimedArtist } from "features/reviewable";
import { formatWalletAddress } from "utils/wallet";
import { useAuth } from "features/auth";
import { pluralize } from "utils/text";

type RouteParams = { userId: string };

function UserProfile() {
  const { userId } = useParams<RouteParams>();
  const { data: profileUser } = useProfile(userId);
  const { user } = useAuth();
  const isCurrentUser = user?.id === profileUser?.id;

  const {
    data: submissions,
    hasNextPage,
    fetchNextPage,
  } = useOpenCallSubmissions({
    createdByUserId: userId,
    sort: ["createdAt desc"],
    perPage: 6,
  });
  const loadMore = () => fetchNextPage();
  const allSubmissions = submissions?.pages?.flatMap((page) => page.data);

  if (!profileUser) {
    return null;
  }

  const isPrivate = profileUser?.isPrivate;
  const userIsArtist = profileUser.claimedArtistPageIds?.length !== 0;
  const userIsHugger = !!profileUser.utilities?.find(
    (u) => u === "Hugger" || "HUG Pass",
  );

  return (
    <PublicProfileWrapper>
      <Head
        title={`${profileUser.name}'s Profile on HUG`}
        type="profile"
        username={profileUser?.name}
        socialImage={profileUser?.avatar?.url600 ?? profileUser?.avatar?.url}
      />
      <Header>
        <HeaderContainer width="sm">
          <Avatar
            style={{ alignSelf: "flex-start" }}
            src={profileUser.avatar?.url300 ?? profileUser.avatar?.url}
            size={100}
            alt=""
          />
          <Stack gap="xs">
            <H1 size="h3">
              <span>{profileUser.name ?? "Unnamed Hugger"}</span>
              {userIsHugger && <small>Hugger!</small>}
            </H1>

            {!isPrivate && profileUser.wallets[0] && (
              <SCopyToClipboard
                buttonText={formatWalletAddress(profileUser.wallets[0])}
                value={profileUser.wallets[0]}
                successText="Wallet address copied!"
              />
            )}
            {!isPrivate && (
              <Stack gap="xxs">
                <Text size="xs" as="div">
                  {UserStatus(profileUser)}
                </Text>
                <Text size="xs" as="div">
                  <FlexRow gap="8px">
                    <span>
                      {pluralize(profileUser.reviewCount ?? 0, "Review")}
                    </span>
                    {" | "}
                    <span>
                      {`Following ${pluralize(profileUser.followingCount ?? 0, "Artist")}`}
                    </span>
                  </FlexRow>
                </Text>
              </Stack>
            )}
          </Stack>
        </HeaderContainer>
      </Header>{" "}
      <Container width="sm">
        {isPrivate ? (
          <Box>
            <Stack>
              <H2 size="md" textAlign="center">
                This profile is private
              </H2>
            </Stack>
          </Box>
        ) : (
          <Stack gap="lg">
            {userIsArtist && (
              <section>
                <H2 size="md">
                  {!!profileUser.name && (
                    <span>{`${profileUser.name}'s `}</span>
                  )}
                  <span>
                    {pluralize(
                      profileUser?.claimedArtistPageIds?.length,
                      "Artist Profile",
                    )}
                  </span>
                </H2>
                {!!profileUser.claimedArtistPageIds?.length && (
                  <List type="blank">
                    <Stack gap="sm">
                      {profileUser?.claimedArtistPageIds.map((id: string) => (
                        <li key={id}>
                          <ClaimedArtist canEdit={false} id={id} />
                        </li>
                      ))}
                    </Stack>
                  </List>
                )}
              </section>
            )}

            {!!allSubmissions?.length && (
              <section>
                <H2 size="md">
                  {!!profileUser.name && (
                    <span>{`${profileUser.name}'s `}</span>
                  )}
                  <span>Open Call Submissions</span>
                </H2>

                <Stack gap="sm">
                  {allSubmissions.map((submission) => (
                    <OpenCallSubmissionSummary
                      showArtistName={false}
                      userIsSubmitter={isCurrentUser}
                      isSubmissionsOpen={false}
                      showOpenCallLink
                      showCreatedAt={false}
                      {...submission}
                      key={submission.submissionId}
                    />
                  ))}
                  {hasNextPage && <Button onClick={loadMore}>Load More</Button>}{" "}
                </Stack>
              </section>
            )}

            {/* Show only published reviews? */}
            {profileUser.reviewCount > 0 && (
              <section>
                <H2 size="md">
                  {!!profileUser.name && (
                    <span>{`${profileUser.name}'s `}</span>
                  )}
                  <span>
                    Reviews (<span>{profileUser.reviewCount ?? 0}</span>)
                  </span>
                </H2>
                <ProfileReviews showPublicDataOnly userId={profileUser.id} />
              </section>
            )}

            {profileUser.followingCount > 0 && (
              <section>
                <H2 size="md">
                  {!!profileUser.name && (
                    <span>{`${profileUser.name}'s `}</span>
                  )}
                  <span>
                    Follows (<span>{profileUser.followingCount ?? 0}</span>)
                  </span>
                </H2>
                <UserFollows userId={profileUser.id} allowUpdates={false} />
              </section>
            )}
          </Stack>
        )}
      </Container>
    </PublicProfileWrapper>
  );
}

const PublicProfileWrapper = styled.div({
  padding: "0 0 5rem",
});

const Header = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.fg05,
  padding: "2rem 0",
  marginBottom: "3rem",
}));

const HeaderContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  lineHeight: 1.15,
  textAlign: "center",
  gap: 10,
  img: {
    display: "block",
    margin: "0 auto",
    [theme.breakpoints.tablet]: {
      margin: 0,
    },
  },
  h1: {
    color: theme.colors.fg,
  },
  [theme.breakpoints.tablet]: {
    flexDirection: "row",
    justifyContent: "center",
    textAlign: "left",
    gap: "1em",
    "&>*": {
      transform: "translateX(-1em)",
    },
  },
})).withComponent("header");

const SCopyToClipboard = styled(CopyToClipboard)(({ theme }) => ({
  [theme.breakpoints.maxTablet]: {
    "& div": {
      justifyContent: "center",
    },
  },
}));

export default UserProfile;
